<script>
  // Zutatsuche der Eingangsmaske (A7/L3).
  //
  // Vier Quellen aus lagerZutatSuche(), zwei Gruppen in der Anzeige — „Im
  // Lager" und „Bekannte Zutaten". Der Moment, in dem der Betriebsstamm
  // entsteht: Ein Treffer ohne Kennung legt beim Buchen die Zutat an, und das
  // steht dran.
  //
  // Ein Name ohne Treffer ist ein eigener Vorschlag. Weil der Katalog dann
  // weder Warengruppe noch Rechenrolle mitliefert, wird die Warengruppe hier
  // gewählt — sonst entstünde ein Stammsatz ohne Zuordnung.
  import { onMount, tick } from 'svelte';

  // `nurStamm` (L10, Abschluss-Maske): nur Zutaten, die es im Betrieb schon
  // gibt. Anlegen braucht die Cloud — und ein Backtag-Abschluss in der
  // Backstube darf nicht am Netz hängen. Eine Zutat, die nie eingebucht wurde,
  // gehört ohnehin nicht abgebucht.
  let { onwahl, nurStamm = false } = $props();

  let query    = $state('');
  let markiert = $state(0);
  let freiWahl = $state(null);   // gewählter freier Treffer, wartet auf Gruppe
  let gruppe   = $state('Sonstiges');
  // Drei Wege hinaus statt eines: Escape, das Kreuz im Feld, ein Klick daneben.
  // Vorher blieb die Liste offen, bis der Text zeichenweise weggelöscht war.
  let offen    = $state(true);
  let wurzel;
  let feld;
  let feldBox;
  // Die Suche steht am ENDE einer Liste, die mit jeder Position wächst. Auf
  // dem Handy lag sie ab der dritten Zutat mitsamt Vorschlägen hinter der
  // Tastatur; wer hochrollte, schloss dabei das Dropdown. Sie gehört an den
  // OBEREN Rand des sichtbaren Bereichs, und dafür braucht es zweierlei:
  //   raum      — ein letztes Element lässt sich nicht nach oben rollen, der
  //               Scrollbereich ist dahinter zu Ende. Solange die Suche offen
  //               ist, fordert sie deshalb die Höhe des Sichtfensters an.
  //   sichtRaum — wie viel unter dem Feld WIRKLICH frei ist, weiß nur das
  //               visuelle Viewport: Die Tastatur verkleinert es, ohne dass
  //               sich an vh oder innerHeight etwas ändert.
  let raum      = $state(0);
  let sichtRaum = $state(0);
  // $state, weil der Effekt unten sie LIEST: Ein einfaches `let` benachrichtigt
  // ihn nicht, wenn bind:this es setzt.
  let liste = $state(null);

  const t = (k, p) => window.t(k, p);
  // Mit `nurStamm` wird weiter gesucht und danach gekürzt: Die ersten acht
  // Treffer können Katalogeinträge sein, hinter denen die Stammzutat stünde.
  let treffer = $derived(offen && query.trim()
    ? (nurStamm
        ? window.lagerZutatSuche(query, 40).filter((e) => e.zutatId).slice(0, 8)
        : window.lagerZutatSuche(query))
    : []);
  let imLager  = $derived(treffer.filter((e) => e.quelle === 'lager'));
  let bekannt  = $derived(treffer.filter((e) => e.quelle === 'stamm' || e.quelle === 'rezept' || e.quelle === 'katalog'));
  let frei     = $derived(treffer.find((e) => e.quelle === 'frei') || null);
  let flach    = $derived([...imLager, ...bekannt, ...(frei ? [frei] : [])]);

  function waehle(e) {
    if (e.quelle === 'frei') { freiWahl = e; return; }
    onwahl(e);
    query = ''; markiert = 0; freiWahl = null; offen = true;
  }

  /** Leert das Feld und schließt die Liste — der Fokus bleibt, wo er ist. */
  function leeren() {
    query = ''; markiert = 0; offen = true;
    feld?.focus();
  }

  function schliessen() {
    // Erst die Liste, dann das Feld: Wer einmal Escape drückt, will die Liste
    // los und nicht seine Eingabe. Ein zweites Escape leert.
    if (offen && query.trim()) { offen = false; return; }
    query = ''; offen = true;
  }

  // Die Markierung im Blick behalten. Ohne das lief sie bei vielen Treffern
  // aus dem sichtbaren Bereich, und man sah nicht mehr, was ausgewählt ist.
  // `nearest` rollt nur so weit wie nötig — die Liste springt nicht.
  $effect(() => {
    const _ = markiert;               // Abhängigkeit, damit der Effekt läuft
    if (!offen || !liste) return;
    const el = liste.querySelectorAll('.lg-item')[markiert];
    el?.scrollIntoView({ block: 'nearest' });
  });

  /** Der nächste rollende Vorfahr — der Dialog, nie das Dokument. */
  function scrollBox() {
    for (let p = wurzel?.parentElement; p; p = p.parentElement) {
      const o = getComputedStyle(p).overflowY;
      if (o === 'auto' || o === 'scroll') return p;
    }
    return null;
  }

  /**
   * Die sichtbare Unterkante, in Layout-Koordinaten. Zwei Dinge begrenzen sie,
   * und es gilt die obere von beiden: der Rand des Scrollbereichs (darunter
   * liegt der Sammelknopf) und die Oberkante der Tastatur. Letztere kennt nur
   * das visuelle Viewport — an vh und innerHeight ändert sie nichts.
   * @param {Element|null} box
   */
  function sichtUnten(box) {
    const vv = window.visualViewport;
    return Math.min(box ? box.getBoundingClientRect().bottom : Infinity,
                    vv ? vv.height + vv.offsetTop : window.innerHeight);
  }

  /**
   * Freie Höhe unter dem Feld — der Deckel für die Vorschlagsliste.
   * @param {number} [unten] Unterkante des Feldes; ohne Angabe die aktuelle.
   */
  function messen(unten) {
    if (!feldBox) return;
    const kante = unten ?? feldBox.getBoundingClientRect().bottom;
    sichtRaum = Math.max(140, Math.round(sichtUnten(scrollBox()) - kante - 12));
  }

  /**
   * Höhe der klebenden Kopfzeile „Positionen" — die einzige Zeile, die beim
   * Rollen stehen bleibt. Gemessen statt aus dem CSS gelesen: Auf dem Desktop
   * ist sie anders hoch als auf dem Handy, und im Leerzustand gibt es sie gar
   * nicht.
   * @param {Element} box
   */
  function kopfHoehe(box) {
    const kopf = [...box.children].find((el) => getComputedStyle(el).position === 'sticky');
    return kopf ? kopf.getBoundingClientRect().height : 0;
  }

  /**
   * Feld und Vorschläge ins Bild holen: ganz nach oben, bis nur noch der
   * Suchschlitz steht. Wer sucht, will die Vorschläge sehen — die schon
   * erfassten Positionen stehen in diesem Moment nur im Weg, und jede Zeile,
   * die von ihnen bleibt, fehlt der Trefferliste.
   * @param {boolean} [sanft=true]
   */
  async function anDenAnfang(sanft = true) {
    // Erst abwarten: Mit dem Öffnen der Suche klappt der Kopf der Maske ein
    // (mobil), und der Scrollbereich wird dabei deutlich höher. Vor dem Flush
    // gemessen wäre jede Zahl hier die von gestern.
    await tick();
    const box = scrollBox();
    if (!box || !feldBox) { messen(); return; }
    // Gerollt wird, bis der BLOCK bündig unter der Kopfzeile sitzt — nicht
    // das Feld. So bleibt kein Streifen der letzten Position übrig, und im
    // Leerzustand bleibt die Beschriftung über dem Feld stehen, wo sie hingehört.
    const block = wurzel?.parentElement ?? feldBox;
    const kopfH = kopfHoehe(box);
    const feldR = feldBox.getBoundingClientRect();
    const weit  = block.getBoundingClientRect().top - box.getBoundingClientRect().top - kopfH;
    // Steht der Block schon oben, ist nichts zu tun — im Leerzustand IST die
    // Suche die Maske und hat nichts über sich, das wegzurollen wäre.
    if (weit <= 1) { messen(); return; }

    // Ein letztes Element lässt sich nicht nach oben rollen — erst Platz
    // schaffen, dann rollen. Der eigene Innenabstand des Blocks zählt nicht
    // mit, sonst liesse sich am Ende ein leerer Streifen darunter wegrollen.
    const stil = getComputedStyle(block);
    const pad  = (parseFloat(stil.paddingTop) || 0) + (parseFloat(stil.paddingBottom) || 0);
    raum = Math.max(0, box.clientHeight - kopfH - pad);
    await tick();
    box.scrollTo({ top: box.scrollTop + weit, behavior: sanft ? 'smooth' : 'auto' });
    // Wo das Feld DANACH steht, ist ausgerechnet — messen statt auf das Ende
    // einer weichen Rollbewegung zu warten.
    messen(feldR.bottom - weit);
  }

  // Beim Aufklappen des Dropdowns noch einmal — wer die Suche vorhin geöffnet
  // und zwischendurch gerollt hat, fände sie sonst irgendwo im Bild.
  let warOffen = false;
  $effect(() => {
    const zeigt = flach.length > 0;
    if (zeigt && !warOffen) anDenAnfang();
    warOffen = zeigt;
  });

  // Die Tastatur meldet sich nicht an; sie ändert nur das visuelle Viewport.
  $effect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const auf = () => messen();
    vv.addEventListener('resize', auf);
    vv.addEventListener('scroll', auf);
    return () => { vv.removeEventListener('resize', auf); vv.removeEventListener('scroll', auf); };
  });

  // Fokus und Sichtbarkeit selbst setzen statt über `autofocus`: Der Browser
  // rollt ein automatisch fokussiertes Feld so weit nach oben, dass es unter
  // der klebenden Kopfzeile „Positionen" landete und nur halb zu sehen war.
  // `scroll-margin-top` unten hält den Platz frei, `nearest` rollt nur so weit
  // wie nötig.
  onMount(() => {
    feld?.focus({ preventScroll: true });
    // Ohne Sanftheit: Die Suche ist gerade erst erschienen, es gibt keine
    // Bewegung zu zeigen — nur eine Ausgangslage herzustellen.
    anDenAnfang(false);
  });

  // Ein Klick daneben schließt ebenfalls — die Liste soll nicht im Weg stehen,
  // wenn jemand oben das Datum ändern will.
  function draussen(ev) {
    if (wurzel && !wurzel.contains(ev.target)) offen = false;
  }
  $effect(() => {
    document.addEventListener('pointerdown', draussen, true);
    return () => document.removeEventListener('pointerdown', draussen, true);
  });

  function freiBestaetigen() {
    onwahl({ ...freiWahl, warengruppe: gruppe });
    query = ''; markiert = 0; freiWahl = null; gruppe = 'Sonstiges';
  }

  // Tastatur: Die Maske wird oft mit einer Hand am Lieferschein bedient.
  function taste(ev) {
    // Cmd/Ctrl+Enter gehört dem Formular — hier durchlassen.
    if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) return;
    if (freiWahl) return;
    if (ev.key === 'ArrowDown') { ev.preventDefault(); markiert = Math.min(markiert + 1, flach.length - 1); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); markiert = Math.max(markiert - 1, 0); }
    else if (ev.key === 'Enter') { ev.preventDefault(); if (flach[markiert]) waehle(flach[markiert]); }
    else if (ev.key === 'Escape') { ev.preventDefault(); ev.stopPropagation(); schliessen(); }
  }

  /**
   * Den Namen in Abschnitte zerlegen — getroffen oder nicht.
   *
   * Gesucht wird nach der ganzen Eingabe UND nach ihren einzelnen Woertern,
   * weil die Suche selbst es so haelt: „weizen 550" findet „Weizenmehl 550"
   * ueber zwei Woerter, und dann gehoeren auch beide markiert. Ohne das stuende
   * der Treffer als einziger ohne Hervorhebung in der Liste — als waere er
   * zufaellig dabei.
   *
   * Das Laengste zuerst: Wo die ganze Eingabe am Stueck steht, soll sie EINE
   * Markierung sein und nicht in ihre Woerter zerfallen.
   *
   * @param {string} name
   * @returns {{ text: string, hit: boolean }[]}
   */
  function hervor(name) {
    const q = query.trim();
    if (!q) return [{ text: name, hit: false }];
    const nadeln = [...new Set([q, ...q.split(/\s+/)])]
      .filter(Boolean).map((n) => n.toLowerCase())
      .sort((a, b) => b.length - a.length);
    const klein = name.toLowerCase();
    const teile = [];
    let rest = '';
    for (let i = 0; i < name.length;) {
      const n = nadeln.find((x) => klein.startsWith(x, i));
      if (!n) { rest += name[i]; i += 1; continue; }
      if (rest) { teile.push({ text: rest, hit: false }); rest = ''; }
      teile.push({ text: name.slice(i, i + n.length), hit: true });
      i += n.length;
    }
    if (rest) teile.push({ text: rest, hit: false });
    return teile;
  }

  const menge = (g) => { const m = window.lagerMenge(g); return `${m.wert} ${m.einheit}`; };
</script>

<div bind:this={wurzel} class="lg-such"
     style="{raum ? `min-height:${raum}px;` : ''}{sichtRaum ? `--lg-such-raum:${sichtRaum}px` : ''}">
<div class="lg-such-feld" bind:this={feldBox}>
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
       stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
  <input type="text" bind:value={query} bind:this={feld} onkeydown={taste}
         oninput={() => { offen = true; markiert = 0; }}
         placeholder={t('lager_eingang_such_ph')} aria-label={t('lager_eingang_suchen')}>
  {#if query}
    <button type="button" class="lg-such-x" onclick={leeren} aria-label={t('lager_such_leeren')}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
           stroke-linecap="round" width="16" height="16" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  {/if}
</div>

{#if freiWahl}
  <!-- Ohne Katalogtreffer fehlt die Warengruppe. Sie hier zu wählen ist
       billiger, als sie später in einer Stammpflege nachzutragen. -->
  <div class="lg-frei">
    <div class="lg-frei__titel">{t('lager_frei_anlegen', { name: freiWahl.name })}</div>
    <div class="lg-frei__lab">{t('lager_warengruppe_waehlen')}</div>
    <div class="lg-gruppen">
      {#each window.LAGER_WARENGRUPPEN as g}
        <button type="button" class:is-on={gruppe === g} onclick={() => (gruppe = g)}>{g}</button>
      {/each}
    </div>
    <button type="button" class="lg-frei__ok" onclick={freiBestaetigen}>
      {t('lager_eingang_weitere')}
    </button>
  </div>
{:else if flach.length}
  <div class="lg-vorschlag" role="listbox" bind:this={liste}>
    {#if imLager.length}<div class="lg-vorschlag__gruppe">{t('lager_gruppe_lager')}</div>{/if}
    {#each imLager as e, i}
      {@const teile = hervor(e.name)}
      <button type="button" class="lg-item" class:is-on={flach[markiert] === e} onclick={() => waehle(e)}>
        <span class="lg-item__name">{#each teile as st}{#if st.hit}<b>{st.text}</b>{:else}{st.text}{/if}{/each}</span>
        <span class="lg-item__wert">{menge(e.bestand ?? 0)}</span>
      </button>
    {/each}

    {#if bekannt.length}<div class="lg-vorschlag__gruppe">{t('lager_gruppe_bekannt')}</div>{/if}
    {#each bekannt as e}
      {@const teile = hervor(e.name)}
      <button type="button" class="lg-item" class:is-on={flach[markiert] === e} onclick={() => waehle(e)}>
        <span>
          <span class="lg-item__name">{#each teile as st}{#if st.hit}<b>{st.text}</b>{:else}{st.text}{/if}{/each}</span>
          {#if e.warengruppe}<span class="lg-item__hint">{e.warengruppe}</span>{/if}
        </span>
        {#if !e.zutatId}<span class="lg-neu">{t('lager_neu_badge')}</span>{/if}
      </button>
    {/each}

    {#if frei}
      <button type="button" class="lg-item lg-item--frei" class:is-on={flach[markiert] === frei}
              onclick={() => waehle(frei)}>
        <span class="lg-item__name">{t('lager_frei_anlegen', { name: frei.name })}</span>
        <span class="lg-neu">{t('lager_neu_badge')}</span>
      </button>
    {/if}
  </div>
{/if}
</div>

<style>
  .lg-such-feld {
    /* Höhe der klebenden Kopfzeile „Positionen" plus Luft: Ohne das rollt der
       Browser das Feld beim Fokussieren unter sie. */
    scroll-margin-top: 56px;
    scroll-margin-bottom: 12px;
    display: flex; align-items: center; gap: 10px;
    min-height: 52px; padding: 0 14px;
    background: var(--lg-card); border: 1px solid var(--lg-ink);
    border-radius: 10px; box-shadow: 0 0 0 3px rgba(41, 37, 36, 0.07);
    color: var(--lg-ink-mute);
  }
  .lg-such-feld input {
    flex: 1; min-width: 0; border: 0; background: none; outline: none;
    font: inherit; font-size: 15px; color: var(--lg-ink);
  }
  .lg-such-x {
    flex-shrink: 0; width: 32px; height: 32px; display: flex; align-items: center;
    justify-content: center; border: 0; border-radius: 8px; background: none;
    color: var(--lg-ink-mute); cursor: pointer;
  }
  .lg-such-x:hover { background: #f5f5f4; color: var(--lg-ink); }
  .lg-such-x:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; }
  /* Die Liste scrollt in SICH, nicht im Dialog. Sonst schöbe eine lange
     Trefferliste den Sammelknopf aus dem Bild, und die Tastaturmarkierung
     wanderte durch die ganze Maske statt durch die Vorschläge. */
  .lg-vorschlag {
    margin-top: 8px; background: var(--lg-card);
    border: 1px solid var(--lg-line); border-radius: 12px;
    /* Der gemessene Raum kennt beide Grenzen — Rand des Scrollbereichs und
       Oberkante der Tastatur — und ist deshalb der bessere Deckel als eine
       feste Zahl. Die feste bleibt der Rückfall für den Augenblick vor der
       ersten Messung. */
    max-height: var(--lg-such-raum, min(46vh, 320px)); overflow-y: auto; overscroll-behavior: contain;
    box-shadow: 0 10px 24px -12px rgba(28, 25, 23, 0.25);
  }
  .lg-vorschlag__gruppe {
    padding: 10px 14px 6px; background: var(--lg-tint);
    border-top: 1px solid var(--lg-line);
    font-size: 10px; font-weight: 700; letter-spacing: 0.09em;
    text-transform: uppercase; color: var(--lg-ink-mute);
  }
  .lg-vorschlag__gruppe:first-child { border-top: 0; }
  .lg-item { scroll-margin: 4px 0; }
  .lg-item {
    display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 10px;
    width: 100%; box-sizing: border-box; min-height: 56px; padding: 10px 14px;
    border: 0; border-top: 1px solid var(--lg-line);
    background: var(--lg-card); text-align: left; font: inherit; cursor: pointer;
  }
  .lg-item:hover, .lg-item.is-on { background: #f5f5f4; }
  .lg-item:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: -2px; background: #f5f5f4; }
  .lg-item__name { font-size: 15px; font-weight: 600; color: var(--lg-ink); }
  .lg-item__name b { color: #1c1917; background: #faf2e2; border-radius: 2px; }
  .lg-item__hint { display: block; font-size: 11.5px; font-weight: 400; color: var(--lg-ink-soft); margin-top: 2px; }
  .lg-item__wert { font-size: 13px; color: var(--lg-ink-soft); font-variant-numeric: tabular-nums; }
  .lg-item--frei .lg-item__name { font-weight: 500; color: var(--lg-ink-soft); }
  .lg-neu {
    white-space: nowrap; display: inline-flex; align-items: center;
    padding: 2px 8px; border-radius: 999px; background: #e8eee0; color: #4d5c3b;
    font-size: 10px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
  }
  .lg-frei {
    margin-top: 8px; padding: 14px; background: var(--lg-card);
    border: 1px solid var(--lg-line); border-radius: 12px;
  }
  .lg-frei__titel { font-size: 14px; font-weight: 600; color: var(--lg-ink); }
  .lg-frei__lab {
    margin-top: 12px; font-size: 11px; font-weight: 700; letter-spacing: 0.07em;
    text-transform: uppercase; color: var(--lg-ink-mute);
  }
  .lg-gruppen { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  .lg-gruppen button {
    min-height: 32px; padding: 0 10px; border: 1px solid var(--lg-line);
    border-radius: 999px; background: var(--lg-card); font: inherit;
    font-size: 11.5px; font-weight: 600; color: var(--lg-ink-soft); cursor: pointer;
  }
  .lg-gruppen button.is-on { background: var(--lg-ink); border-color: var(--lg-ink); color: #fff; }
  .lg-gruppen button:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }
  .lg-frei__ok {
    width: 100%; min-height: 44px; margin-top: 12px; border: 0; border-radius: 10px;
    background: var(--lg-ink); color: #fff; font: inherit; font-size: 14px;
    font-weight: 700; cursor: pointer;
  }
  .lg-frei__ok:focus-visible { outline: 2px solid var(--lg-ink); outline-offset: 2px; }
</style>
